// Firebase configuration for push notifications
import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, Messaging } from 'firebase/messaging';
import { getAuth, GoogleAuthProvider, FacebookAuthProvider, OAuthProvider, signInWithPopup } from 'firebase/auth';

// Helper to remove invisible BOM characters and whitespace that Vercel might inject from .env files
const sanitizeEnv = (val: string | undefined) => {
    if (!val) return val;
    return val.replace(/^\uFEFF/, '').trim();
};

// Firebase configuration - Replace with your own config from Firebase Console
const firebaseConfig = {
    apiKey: sanitizeEnv(import.meta.env.VITE_FIREBASE_API_KEY),
    authDomain: sanitizeEnv(import.meta.env.VITE_FIREBASE_AUTH_DOMAIN),
    projectId: sanitizeEnv(import.meta.env.VITE_FIREBASE_PROJECT_ID),
    storageBucket: sanitizeEnv(import.meta.env.VITE_FIREBASE_STORAGE_BUCKET),
    messagingSenderId: sanitizeEnv(import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID),
    appId: sanitizeEnv(import.meta.env.VITE_FIREBASE_APP_ID)
};

// Initialize Firebase
let app: ReturnType<typeof initializeApp>;
let auth: ReturnType<typeof getAuth>;
let messaging: Messaging | null = null;

try {
    app = initializeApp(firebaseConfig);
    auth = getAuth(app);

    // Only initialize messaging in browser environment
    if (typeof window !== 'undefined' && 'Notification' in window) {
        try {
            messaging = getMessaging(app);
        } catch (msgError) {
            console.warn('Firebase Messaging initialization failed:', msgError);
        }
    }
} catch (error) {
    console.error('Firebase initialization error:', error);
}

export const initializeFirebase = () => {
    return { app, messaging, auth };
};

// Get FCM token for the current user
export const getFCMToken = async (): Promise<string | null> => {
    try {
        if (!messaging) {
            console.warn('Messaging not available');
            return null;
        }

        // Request notification permission
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
            console.warn('Notification permission denied');
            return null;
        }

        // Get the token
        // Note: In production, you need a valid VAPID key from Firebase Console
        // You should probably load this from env as well
        const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY || 'YOUR_VAPID_KEY'; // Added env var support

        const token = await getToken(messaging, {
            vapidKey: vapidKey
        });

        return token;
    } catch (error) {
        console.error('Error getting FCM token:', error);
        return null;
    }
};

// Listen for foreground messages
export const onForegroundMessage = (callback: (payload: any) => void) => {
    if (messaging) {
        return onMessage(messaging, (payload) => {
            console.log('Foreground message received:', payload);
            callback(payload);
        });
    }
    return () => { }; // Return empty unsubscribe function
};

// Simulated push notification (for demo without real Firebase backend)
export const simulateLocalNotification = (title: string, body: string, icon?: string) => {
    if ('Notification' in window && Notification.permission === 'granted') {
        new Notification(title, {
            body,
            icon: icon || '/vite.svg',
            badge: '/vite.svg',
            tag: 'booking-notification',
            requireInteraction: true
        });
    }
};

export { app, messaging, auth };
